import { ImageLoaderProps } from 'next/image';
import { twMerge } from 'tailwind-merge';

import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { getImgProxyLoader } from '@lib/images/imgProxyLoader';
import { EventMykissCardClicked } from '@generated/events';
import { FishCatchesIcon } from '../../../Icons/FishCatchesIcon';
import { NextImageWrapper } from '../../../NextImage/NextImageWrapper';
import { LinkCard } from '../../../Cards/LinkCard/LinkCard';
import { ChevronRightIcon } from '../../../Icons/ChevronRightIcon';

interface IProps {
  name: string;
  speciesId: string;
  /* An identifier for where this carousel is being used, e.g. species-top-waters */
  clickEventSource: EventMykissCardClicked['properties']['source'];
  /* An ID of the resource type where this carousel is being used, e.g. a species ID if it's on a species page */
  clickEventSourceId?: EventMykissCardClicked['properties']['source_id'];
  /* The position of the card in a list (if applicable) */
  clickEventPosition?: EventMykissCardClicked['properties']['position'];
  speciesGroupUrl: string;
  className?: string;
  linkClassName?: string;
  imageClassName?: string;
  imageUrl?: string;
  imageWidth?: number;
  imageHeight?: number;
  useCustomLoader?: boolean;
  testId?: string;
}

export const SpeciesGroupCard = ({
  speciesId,
  name,
  clickEventSource,
  clickEventPosition,
  clickEventSourceId,
  className = '',
  linkClassName,
  speciesGroupUrl,
  imageClassName = '',
  imageUrl,
  imageWidth,
  imageHeight,
  useCustomLoader = true,
  testId,
}: IProps) => {
  const { currentPage } = useAnalytics();
  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, imageWidth ?? 0, imageHeight, { quality });

  return (
    <LinkCard
      to={speciesGroupUrl}
      withDefaults={false}
      eventProperties={{
        source: clickEventSource,
        target: 'species',
        target_id: speciesId,
        position: clickEventPosition,
        source_id: clickEventSourceId,
        page: currentPage,
      }}
      linkClassName={linkClassName}
      className={twMerge(
        'border-b-twilight relative w-full border-b py-3 pr-2 md:py-2.5',
        className,
      )}
      testId={testId}
    >
      <div className="relative flex items-center gap-4">
        {imageUrl && imageWidth && imageHeight ? (
          <NextImageWrapper
            wrapperClassName="w-32 h-12 md:h-11 md:w-28"
            imageClassName={twMerge('size-full object-contain', imageClassName)}
            customPlaceholderIcon={
              <FishCatchesIcon className="size-full object-contain" size={93} />
            }
            customPlaceholderClassName="text-twilight p-0 bg-sea-foam sm:bg-lake-fog sm:text-twilight"
            alt={name}
            src={imageUrl}
            loader={useCustomLoader ? imgProxyLoader : undefined}
            fill
            sizes="(min-width: 990px) 128vw, 112px"
          />
        ) : (
          <div className="bg-lake-fog text-twilight flex h-11 w-28 items-center justify-center rounded-md md:rounded-lg">
            <FishCatchesIcon width={112} height={44} />
          </div>
        )}
        <div className="flex grow flex-col gap-3 md:gap-2">{name}</div>
        <div className="text-abyss mr-1.5">
          <ChevronRightIcon />
        </div>
      </div>
    </LinkCard>
  );
};
